/**
 * RelationshipsPage
 *
 * Dedicated page for viewing saved relationships with synastry summaries
 * and quick access to daily transits.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks';
import {
  getRelationships,
  getRelationshipTransits,
  regenerateSummary,
  deleteRelationship
} from '../api/relationships';
import type { Relationship } from '../types';
import './RelationshipsPage.css';


export function RelationshipsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loadingTransits, setLoadingTransits] = useState<number | null>(null);
  const [transitsData, setTransitsData] = useState<Record<number, Record<string, unknown>>>({});
  const [regeneratingId, setRegeneratingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadRelationships = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const data = await getRelationships();
      setRelationships(data);
    } catch (error) {
      console.error('Failed to load relationships:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadRelationships();
  }, [loadRelationships]);

  const handleLoadTransits = async (id: number) => {
    // Check if we already have cached transits for this relationship
    const rel = relationships.find(r => r.id === id);
    if (rel?.cached_transits) {
      setTransitsData(prev => ({ ...prev, [id]: rel.cached_transits! }));
      setExpandedId(expandedId === id ? null : id);
      return;
    }

    setLoadingTransits(id);
    try {
      const transits = await getRelationshipTransits(id);
      setTransitsData(prev => ({ ...prev, [id]: transits }));
      setExpandedId(id);
    } catch (error) {
      console.error('Failed to load transits:', error);
    } finally {
      setLoadingTransits(null);
    }
  };

  const handleRegenerateSummary = async (id: number) => {
    setRegeneratingId(id);
    try {
      await regenerateSummary(id);
      // Reload relationships to get updated summary
      await loadRelationships();
    } catch (error) {
      console.error('Failed to regenerate summary:', error);
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(t('relationships.deleteConfirm', { name }))) return;

    setDeletingId(id);
    try {
      await deleteRelationship(id);
      setRelationships(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Failed to delete:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleAskAbout = (rel: Relationship) => {
    // Navigate to chat with a pre-filled question
    navigate(`/?q=Kakvi su tranziti danas između mene i ${rel.name}?`);
  };

  if (!isAuthenticated) {
    return (
      <div className="relationships-page">
        <div className="empty-state">
          <h2>{t('auth.login')}</h2>
          <p>{t('profile.loginRequired')}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="relationships-page">
        <div className="loading-state">
          <p>{t('relationships.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relationships-page">
      <div className="relationships-header">
        <h1>{t('relationships.title')}</h1>
        <p className="relationships-subtitle">
          {t('relationships.subtitle')}
        </p>
      </div>

      {relationships.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">{t('relationships.empty.icon')}</div>
          <h3>{t('relationships.empty.title')}</h3>
          <p>{t('relationships.empty.description')}</p>
          <button className="primary-btn" onClick={() => navigate('/')}>
            {t('relationships.empty.cta')}
          </button>
        </div>
      ) : (
        <div className="relationships-grid">
          {relationships.map(rel => (
            <div key={rel.id} className="relationship-card">
              <div className="relationship-card-header">
                <div className="relationship-identity">
                  <h3>{rel.name}</h3>
                  <span className="relationship-type-badge">
                    {t(`profile.relationshipTypes.${rel.relationship_type}`, rel.relationship_type)}
                  </span>
                </div>
                <div className="relationship-signs">
                  {rel.sun_sign && <span title="Sunce">☉ {rel.sun_sign}</span>}
                  {rel.moon_sign && <span title="Mesec">☽ {rel.moon_sign}</span>}
                  {rel.asc_sign && <span title="Ascendent">ASC {rel.asc_sign}</span>}
                </div>
              </div>

              {/* Summary Section */}
              {rel.has_summary && rel.synastry_summary ? (
                <div className="relationship-summary">
                  <p className="summary-snippet">{rel.summary_snippet}</p>

                  <div className="summary-details">
                    <div className="summary-section strengths">
                      <h4>✨ {t('relationships.card.strengths')}</h4>
                      <ul>
                        {rel.synastry_summary.strengths?.slice(0, 3).map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="summary-section challenges">
                      <h4>⚡ {t('relationships.card.challenges')}</h4>
                      <ul>
                        {rel.synastry_summary.challenges?.slice(0, 3).map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Dimension Bars */}
                  {rel.synastry_summary.dimensions && (
                    <div className="dimension-scores">
                      {Object.entries(rel.synastry_summary.dimensions).map(([key, value]) => {
                        const label = rel.synastry_summary?.dimension_labels?.[key];
                        return (
                          <div key={key} className="dimension-row">
                            <span className="dimension-label">
                              {label?.emoji || '•'} {label?.label || key}
                            </span>
                            <div className="dimension-bar-container">
                              <div
                                className="dimension-bar"
                                style={{ width: `${value}%` }}
                                data-score={value}
                              />
                            </div>
                            <span className="dimension-value">{value}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {rel.synastry_summary.compatibility_score && (
                    <div className="compatibility-score">
                      <span className="score-label">{t('relationships.card.compatibility')}:</span>
                      <span className="score-value">{Math.round(rel.synastry_summary.compatibility_score)}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relationship-no-summary">
                  <p>{t('relationships.card.noSummary')}</p>
                  <button
                    className="regenerate-btn"
                    onClick={() => handleRegenerateSummary(rel.id)}
                    disabled={regeneratingId === rel.id}
                  >
                    {regeneratingId === rel.id ? t('relationships.card.generating') : t('relationships.card.regenerate')}
                  </button>
                </div>
              )}

              {/* Transits Section */}
              <div className="relationship-transits">
                <button
                  className="transits-btn"
                  onClick={() => handleLoadTransits(rel.id)}
                  disabled={loadingTransits === rel.id}
                >
                  {loadingTransits === rel.id
                    ? t('relationships.card.loadingTransits')
                    : `📅 ${t('relationships.card.transits')}`}
                </button>

                {expandedId === rel.id && transitsData[rel.id] && (
                  <div className="transits-content">
                    {transitsData[rel.id].workspace ? (
                      <pre className="transits-workspace">
                        {transitsData[rel.id].workspace as string}
                      </pre>
                    ) : transitsData[rel.id].error ? (
                      <p className="transits-error">{t('relationships.card.transitError')}</p>
                    ) : (
                      <p className="transits-empty">{t('relationships.card.noTransits')}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="relationship-actions">
                <button
                  className="action-btn ask"
                  onClick={() => handleAskAbout(rel)}
                  title={t('relationships.card.askLucy')}
                >
                  💬 {t('relationships.card.askLucy')}
                </button>
                <button
                  className="action-btn delete"
                  onClick={() => handleDelete(rel.id, rel.name)}
                  disabled={deletingId === rel.id}
                  title={t('relationships.card.delete')}
                >
                  {deletingId === rel.id ? '...' : '🗑️'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RelationshipsPage;
